import { Capacitor, registerPlugin, WebPlugin } from "@capacitor/core";

/**
 * WHERE THE SESSION LIVES.
 *
 * On device this is the iOS Keychain, through a plugin that lives IN THIS PROJECT
 * (`ios/App/App/SecureStorePlugin.swift`) rather than in node_modules. That is deliberate
 * and was not a preference: the obvious package ships CocoaPods support only, while this
 * project's native target is Swift Package Manager. Capacitor reports such a plugin as
 * "found" and silently leaves it out of the build, so the call fails on a real device with
 * "not implemented" — visible as signing in working and the user being signed out again on
 * the next launch. Sixty lines of Swift removes the whole class of problem.
 *
 * On the web it is localStorage, and pretending otherwise would be theatre: there is no
 * keychain in a browser. The web build's session is short-lived for that reason.
 */
export interface SecureStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
  clear(): Promise<void>;
}

interface SecureStorePlugin {
  get(options: { key: string }): Promise<{ value: string | null }>;
  set(options: { key: string; value: string }): Promise<void>;
  remove(options: { key: string }): Promise<void>;
  clear(): Promise<void>;
}

/**
 * The web implementation is registered WITH the plugin, so `registerPlugin` resolves it in a
 * browser instead of throwing. This is the pattern for every native capability added later.
 */
class SecureStoreWeb extends WebPlugin implements SecureStorePlugin {
  private prefix = "secure.";

  async get({ key }: { key: string }) {
    try {
      return { value: globalThis.localStorage?.getItem(this.prefix + key) ?? null };
    } catch {
      return { value: null };
    }
  }
  async set({ key, value }: { key: string; value: string }) {
    globalThis.localStorage?.setItem(this.prefix + key, value);
  }
  async remove({ key }: { key: string }) {
    globalThis.localStorage?.removeItem(this.prefix + key);
  }
  async clear() {
    for (const key of Object.keys(globalThis.localStorage ?? {})) {
      if (key.startsWith(this.prefix)) globalThis.localStorage.removeItem(key);
    }
  }
}

const SecureStorePlugin = registerPlugin<SecureStorePlugin>("SecureStore", {
  web: () => new SecureStoreWeb(),
});

export const secureStore: SecureStore = {
  async get(key) {
    const { value } = await SecureStorePlugin.get({ key });
    return value ?? null;
  },
  async set(key, value) {
    await SecureStorePlugin.set({ key, value });
  },
  async remove(key) {
    await SecureStorePlugin.remove({ key });
  },
  async clear() {
    await SecureStorePlugin.clear();
  },
};

/** True where the Keychain is real. Useful for deciding how long a session may live. */
export const secureStoreIsHardwareBacked = Capacitor.isNativePlatform();
